import { equals } from 'ramda';
import { BindDeleteAPICall } from 'reactApp/api/billing/bind/BindDeleteAPICall';
import { BindListAPICall } from 'reactApp/api/billing/bind/BindListAPICall';
import { autoRenewalBannerEnabled, unbindCardBannerEnabled } from 'reactApp/appHelpers/featuresHelpers/features/autoRenewalCommunication';
import { cardUnlinkOutflowWeb } from 'reactApp/appHelpers/featuresHelpers/features/cardUnlinkOutflow';
import { HAS_CARD_FLAG_SYNC_STORAGE_KEY, isHasCardFlagExist, updateHasCardFlag } from 'reactApp/modules/creditCard/creditCard.flags';
import { CardActions, normalizeCardData } from 'reactApp/modules/creditCard/creditCard.module';
import { CardSelectors } from 'reactApp/modules/creditCard/creditCard.selectors';
import { type IApiCard, type IApiLoadResponse, ECardNotifications } from 'reactApp/modules/creditCard/creditCard.types';
import { CreditCardDmrLinkActions } from 'reactApp/modules/creditCardDmrLink/creditCardDmrLink.module';
import { showSnackbarAction } from 'reactApp/modules/snackbar/snackbar.actions';
import { SnackbarTypes } from 'reactApp/modules/snackbar/snackbar.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { safetyPersonalLocalStorage } from 'reactApp/storage';
import { renderCardModal } from 'reactApp/ui/AddCardModal/AddCardModal.helpers';
import { noopVoid } from 'reactApp/utils/helpers';
import ping from 'reactApp/utils/ping';
import { channel } from 'redux-saga';
import { call, cancel, delay, fork, put, race, select, take, takeEvery } from 'redux-saga/effects';

const getCardApiCall = () => new BindListAPICall().makeRequest();

interface IRemoveCardParams {
    bind_id?: string;
    source?: string;
}

function* updateHasCardFlagByCard(card: IApiCard) {
    try {
        yield call(updateHasCardFlag, { state: Boolean(normalizeCardData(card)?.bindId) });
        return true;
    } catch (_) {
        return false;
    }
}

function* updateHasCardFlagByValue(hasCard: boolean) {
    try {
        yield call(updateHasCardFlag, { state: hasCard });
        return true;
    } catch (_) {
        return false;
    }
}

export function* syncCardFlag() {
    if (!autoRenewalBannerEnabled && !unbindCardBannerEnabled) {
        return;
    }

    const isPaidUser = yield select(UserSelectors.isPaidUser);
    if (!isPaidUser) {
        return;
    }

    try {
        const isHasCardFlagSyncDone = safetyPersonalLocalStorage.getItem(HAS_CARD_FLAG_SYNC_STORAGE_KEY) === 'true';
        const isHasCardFlagExists = yield call(isHasCardFlagExist);

        if (isHasCardFlagSyncDone && isHasCardFlagExists) {
            return;
        }

        const isHasCardFlagUpdated = yield call(loadCard);

        if (isHasCardFlagUpdated) {
            safetyPersonalLocalStorage.setItem(HAS_CARD_FLAG_SYNC_STORAGE_KEY, 'true');
        }
    } catch (error) {
        yield put(CardActions.hasError());
    }
}

export function* loadCardIfNotLoaded() {
    const { isLoaded } = yield select(CardSelectors.getLoadingState);

    if (!isLoaded) {
        yield put(CardActions.loadCard());
    }

    return yield handleLoadCardSuccess();
}

export function* handleLoadCardSuccess() {
    const { isLoaded, isLoading } = yield select(CardSelectors.getLoadingState);

    if (isLoaded && !isLoading) {
        return true;
    }

    const { success } = yield race({
        success: take(CardActions.loadCardSuccess),
        fail: take(CardActions.hasError),
    });

    return !!success;
}

export function* loadCard() {
    const isUserWithBilling = yield select(UserSelectors.isUserWithBilling);
    if (!isUserWithBilling) {
        yield cancel();
    }

    try {
        const { data } = yield call(new BindListAPICall().makeRequest);
        const isHasCardFlagUpdated = yield call(updateHasCardFlagByCard, data.card);
        yield put(CardActions.loadCardSuccess(data as IApiLoadResponse));
        return isHasCardFlagUpdated;
    } catch (error) {
        yield put(CardActions.hasError());
        yield cancel();
    }
}

function* removeCard(action) {
    const id = yield select(CardSelectors.getCardId);
    const { onSuccess = noopVoid, source } = action.payload;
    let cardLastDigits = '';
    let snackbarText = 'Карта успешно удалена';

    if (cardUnlinkOutflowWeb) {
        const { card } = yield select(CardSelectors.getCard);
        cardLastDigits = card?.number?.replace(/\D/g, '')?.slice(-4) || '';
        snackbarText = cardLastDigits ? `Карта •••• ${cardLastDigits} отвязана` : 'Карта отвязана';
    }

    const params: IRemoveCardParams = {
        bind_id: id,
    };

    if (source) {
        params.source = source;
    }

    try {
        yield call(() => new BindDeleteAPICall().makeRequest(params));
        yield call(updateHasCardFlagByValue, false);
        yield put(CardActions.removeCardSuccess());

        onSuccess();

        yield put(
            showSnackbarAction({
                type: SnackbarTypes.success,
                text: snackbarText,
                closable: true,
                id: ECardNotifications.removeSuccess,
            })
        );
    } catch (error: any) {
        yield put(CardActions.hasError());

        const { status } = error;

        yield put(
            showSnackbarAction({
                type: SnackbarTypes.failure,
                text: status === 406 ? 'Нет привязанных карт' : 'Не удалось удалить карту',
                closable: true,
                id: ECardNotifications.removeFailure,
            })
        );

        yield cancel();
    }
}

export function* updateCard(action) {
    const { card } = yield select(CardSelectors.getCard);
    const { showNotifications, onSuccess = noopVoid, delay: delayedTime } = action.payload || {};

    const isUserWithBilling = yield select(UserSelectors.isUserWithBilling);
    if (!isUserWithBilling) {
        yield cancel();
    }

    try {
        if (delayedTime) {
            yield delay(delayedTime);
        }

        const { data } = yield ping({
            request: getCardApiCall,
            check: ({ data }) => !equals(normalizeCardData(data.card), card),
        });

        yield call(updateHasCardFlagByCard, data.card);
        yield put(CardActions.updateCardSuccess(data as IApiLoadResponse));

        if (showNotifications) {
            yield put(
                showSnackbarAction({
                    type: SnackbarTypes.success,
                    text: 'Карта обновлена',
                    closable: true,
                    id: ECardNotifications.updateSuccess,
                })
            );
        }

        onSuccess();
    } catch (error) {
        yield put(CardActions.hasError());

        if (showNotifications) {
            yield put(
                showSnackbarAction({
                    type: SnackbarTypes.failure,
                    text: 'Не удалось обновить карту',
                    closable: true,
                    id: ECardNotifications.updateFailure,
                })
            );
        }

        yield cancel();
    }
}

export function* showCardModal(action) {
    const cardChannel = channel();

    const { showNotifications, isSilent, isMobile, onCancel, onSuccess, onUnlinkCard, afterUpdate } = action.payload || {};

    yield renderCardModal({
        onSuccess: () => {
            onSuccess?.();
            cardChannel.put(true);
        },
        onClose: () => cardChannel.close(),
        isMobile,
        onCancel,
        onUnlinkCard,
    });

    const isSuccess = yield take(cardChannel);

    if (isSuccess) {
        yield put(CardActions.updateCard({ showNotifications, isSilent, delay: 1000 }));
        yield take(CardActions.updateCardSuccess);
        yield afterUpdate?.();
    }

    yield put(CreditCardDmrLinkActions.resetLink());
}

export function* watchCreditCard() {
    yield fork(syncCardFlag);
    yield takeEvery(CardActions.loadCardIfNotLoaded.toString(), loadCardIfNotLoaded);
    yield takeEvery(CardActions.loadCard.toString(), loadCard);
    yield takeEvery(CardActions.removeCard.toString(), removeCard);
    yield takeEvery(CardActions.updateCard.toString(), updateCard);
    yield takeEvery(CardActions.showModal.toString(), showCardModal);
}
